import { CountryFields } from '@app/dev/terminal/components/countryFields/countryFields';
import { CurrencyFields } from '@app/dev/terminal/components/currencyFields/currencyFields';
import DatePickerField from '@app/dev/terminal/components/dateBirthSelect';
import { PhoneFields } from '@app/dev/terminal/components/phoneFields';
import { countryCodes as phoneCountryCodes } from '@app/dev/constants/countryPhoneCodes';

export const getPhoneCodeByCountry = (countryName) => {
  if (!countryName) return null;
  const alias = countryName.replace(/\s+/g, '');
  return phoneCountryCodes.find((c) => c.countryAlias === alias)?.phoneCode || null;
};

/**
 * Central registry of every field that can appear in the registration form.
 * Each entry carries its rendering config and its own validate(value, form) function.
 * Steps in conf.js reference field keys from this map — validation follows automatically.
 */
export const fieldRegistry = {
  firstName: {
    name: 'firstName',
    fieldName: 'First Name',
    validate: (value) => {
      if (!value) return 'Field is required';
      if (!/^[A-Za-z]+$/.test(value)) return 'Only letters are allowed';
      return null;
    },
  },

  lastName: {
    name: 'lastName',
    fieldName: 'Last Name',
    validate: (value) => {
      if (!value) return 'Field is required';
      if (!/^[A-Za-z]+$/.test(value)) return 'Only letters are allowed';
      return null;
    },
  },

  birthDate: {
    name: 'birthDate',
    fieldName: 'Date Of Birth',
    customViewComponent: DatePickerField,
    validate: (value) => (!value ? 'Field is required' : null),
  },

  country: {
    name: 'country',
    fieldName: 'Country',
    className: 'country',
    customViewComponent: CountryFields,
    validate: (value) => (!value?.name ? 'Field is required' : null),
    onAfterChange: (value, form) => {
      const phoneCode = getPhoneCodeByCountry(value?.name);
      if (!phoneCode) return null;
      return { phoneNumber: { ...form.phoneNumber, code: phoneCode } };
    },
  },

  currency: {
    name: 'currency',
    fieldName: 'Currency',
    className: 'currency',
    customViewComponent: CurrencyFields,
    validate: (value) => (!value?.name ? 'Field is required' : null)
  },

  email: {
    name: 'email',
    fieldName: 'Email',
    validate: (value) => {
      if (!value) return 'Field is required';
      if (!(/^[\w.-]+@[a-zA-Z\d.-]+\.[a-zA-Z]{2,}$/.test(value))) return 'Invalid email format';
      return null;
    },
  },

  phoneNumber: {
    name: 'phoneNumber',
    fieldName: 'Phone Number',
    className: 'phone-section',
    customViewComponent: PhoneFields,
    validate: (value) => {
      if (!value?.phone) return 'Field is required';
      if (!/^\d+$/.test(value.phone)) return 'Phone number must contain digits only';
      return null;
    },
  },

  street: {
    name: 'street',
    fieldName: 'Street',
    validate: (value) => (!value ? 'Field is required' : null),
  },

  city: {
    name: 'city',
    fieldName: 'City',
    validate: (value) => (!value ? 'Field is required' : null),
  },

  zipCode: {
    name: 'zipCode',
    fieldName: 'Zip-Code',
    validate: (value) => (!value ? 'Field is required' : null),
  },

  userName: {
    name: 'userName',
    fieldName: 'UserName',
    validate: (value) => (!value ? 'Field is required' : null),
  },

  password: {
    name: 'password',
    fieldName: 'Password',
    type: 'password',
    showStrengthMeter: true,
    validate: (value) => (!value ? 'Field is required' : null),
  },

  confirmPassword: {
    name: 'confirmPassword',
    fieldName: 'Confirm Password',
    type: 'password',
    validate: (value, form) => {
      if (!value) return 'Field is required';
      if (value !== form.password) return "Password Didn't match";
      return null;
    },
  },

  rull18: {
    name: 'rull18',
    fieldName: 'rule18',
    fieldType: 'checkbox',
    label: 'I confirm that I am 18 years of age or older.',
    validate: (value) => (!value ? 'Must be checked' : null),
  },

  termsAndConditions: {
    name: 'termsAndConditions',
    fieldName: 'termsAndConditions',
    fieldType: 'checkbox',
    label: 'I have read and agree to the Terms & Conditions and Privacy Policy.',
    validate: (value) => (!value ? 'Must be checked' : null),
  },
};
